import type { ReactNode } from "react";

import clsx from "clsx";
import { useEffect, useId, useState } from "react";

import type { RingGradient } from "./targets";

import styles from "./ringArt.module.css";

interface Props {
    progress: number;
    gradient: RingGradient;
    children: ReactNode;
    isLoading?: boolean;
}

// Doubled vs. the rendered size so the rasterized stroke edges have sub-pixel
// headroom — the SVG is then scaled down by CSS, yielding visibly sharper edges.
const SIZE = 280;
const THICKNESS = 32;
const CENTER = SIZE / 2;
const RADIUS = (SIZE - THICKNESS) / 2;
const TAU = 2 * Math.PI;
const CIRCUMFERENCE = TAU * RADIUS;

export function RingArt({ progress, gradient, children, isLoading = false }: Props) {
    // useId returns IDs containing colons in some React versions; sanitize so
    // they're safe to reference inside SVG `url(#...)`.
    const rawId = useId();
    const baseId = `ringArt-${rawId.replace(/:/g, "")}`;
    const gradientId = `${baseId}-grad`;
    const glowId = `${baseId}-glow`;

    const clamped = Math.max(0, Math.min(100, progress));
    const [displayed, setDisplayed] = useState(0);

    useEffect(() => {
        if (isLoading) {
            setDisplayed(0);
            return;
        }
        // Defer one frame so the initial 0% commits to the DOM before we
        // change to the real value — gives the CSS transition a "from".
        const handle = requestAnimationFrame(() => setDisplayed(clamped));
        return () => cancelAnimationFrame(handle);
    }, [clamped, isLoading]);

    const dashOffset = CIRCUMFERENCE * (1 - displayed / 100);
    const showArc = !isLoading;

    return (
        <div className={clsx(styles.root, isLoading && styles.loading)}>
            <svg className={styles.ring} viewBox={`0 0 ${SIZE} ${SIZE}`} aria-hidden>
                <defs>
                    <linearGradient id={gradientId} x1="0.2" y1="0" x2="0.8" y2="1">
                        <stop offset="0%" style={{ stopColor: gradient.highlight }} />
                        <stop offset="40%" style={{ stopColor: gradient.main }} />
                        <stop offset="100%" style={{ stopColor: gradient.deep }} />
                    </linearGradient>
                    <filter id={glowId} x="-20%" y="-20%" width="140%" height="140%">
                        <feDropShadow dx="0" dy="0" stdDeviation="2" floodColor={gradient.main} floodOpacity="0.35" />
                    </filter>
                </defs>

                <circle
                    cx={CENTER}
                    cy={CENTER}
                    r={RADIUS}
                    fill="none"
                    stroke="var(--mantine-color-dark-7)"
                    strokeWidth={THICKNESS}
                />

                {showArc && (
                    <circle
                        cx={CENTER}
                        cy={CENTER}
                        r={RADIUS}
                        fill="none"
                        stroke={`url(#${gradientId})`}
                        strokeWidth={THICKNESS}
                        strokeDasharray={CIRCUMFERENCE}
                        strokeDashoffset={dashOffset}
                        strokeLinecap="round"
                        transform={`rotate(-90 ${CENTER} ${CENTER})`}
                        filter={`url(#${glowId})`}
                        className={styles.arc}
                    />
                )}
            </svg>
            <div className={styles.center}>{children}</div>
        </div>
    );
}
